import { useEffect, useLayoutEffect, useRef, useState } from "react";
import UserAvatar from "./UserAvatar";
import { Wrench, Eye, ChevronDown, ChevronLeft, ChevronRight, Search, Menu, X } from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import GlobalSearchBar from "./GlobalSearchBar";
import NotificationBell from "./notifications/NotificationBell";
import ViewAsPicker from "./ViewAsPicker";
import logo from "../assets/logo.png";

// Links that require a specific permission; omit `permission` to show always.
// An entry with `children` is a dropdown group instead of a link — it shows
// only if at least one child survives the same filter, and it has no page of
// its own (clicking it opens the menu).
// ⚠️⚠️ EVERY ENTRY IS GATED BY A `section.*.view` PERMISSION — `hideForRoles`
// IS GONE (Sep 10, 2026). It used to hide the Portfolio and Tools groups from
// `technician` by role name, because technicians HOLD jobs.read / visits.read /
// purchase_orders.read (their own screens need them) yet must not see those
// nav entries. That divergence is now a grantable permission of its own, which
// is what let a hardcoded role list be deleted — and what makes "which sections
// can this role see?" answerable for a custom role at all.
//
// Dashboard and Calendar carry markers too, but both tiers hold them
// unconditionally (auth/permissions.py's TIER_FLOOR), so they are always shown.
const NAV_LINKS = [
  { to: "/dashboard", label: "Dashboard", permission: "section.dashboard.view" },
  { to: "/calendar", label: "Calendar", permission: "section.calendar.view" },
  {
    // Renamed from "Directory" (Sep 6, 2026) — the owner's word for this group.
    label: "Portfolio",
    children: [
      { to: "/projects", label: "Projects", permission: "section.projects.view" },
      { to: "/jobs", label: "Jobs", permission: "section.jobs.view" },
      { to: "/visits", label: "Visits", permission: "section.visits.view" },
      // Travel POs are listed and created on /purchase-orders — there is
      // deliberately no separate Travel entry (Sep 6, 2026). /travel redirects.
      { to: "/purchase-orders", label: "Purchase Orders", permission: "section.purchase_orders.view" },
    ],
  },
  // Directory and Office (Oct 26, 2026, owner ask) replace the old Tools group.
  // ⚠️ Customers, Vendors, Users, Rate Cards and Parts Catalog are shortcuts
  // into their Settings pages, which stay where they are (owner: "everything
  // stays in settings"). Each keeps its Settings page's permission.
  {
    label: "Directory",
    children: [
      { to: "/knowledge-base", label: "Knowledge Base", permission: "section.knowledge_base.view" },
      { to: "/org-chart", label: "Org Chart", hideForPartners: true }, // every role but partners
      { to: "/settings?section=users", label: "Users", permission: "section.settings_users.view" },
      { to: "/settings?section=vendors", label: "Vendors", permission: "section.settings_vendors.view", matchPaths: ["/vendors"] },
      { to: "/settings?section=customers", label: "Customers", permission: "section.settings_customers.view", sections: ["properties", "customer-departments"], matchPaths: ["/customers", "/properties"] },
      { to: "/contacts", label: "All Contacts", permission: "section.contacts.view" },
      { to: "/audit", label: "Audit Log", permission: "section.audit.view" },
    ],
  },
  {
    label: "Office",
    children: [
      { to: "/estimates", label: "Pricing", permission: "section.pricing.view" },
      { to: "/enclosure-quotes", label: "Enclosure Quotes", permission: "section.enclosure_quotes.view" },
      { to: "/time-card", label: "Time Card", permission: "section.time_cards.view" },
      { to: "/hr", label: "Human Resources", permission: "section.hr_center.view" },
      { to: "/settings?section=rate-cards", label: "Rate Cards", permission: "section.settings_pricing.view" },
      { to: "/settings?section=parts-catalog", label: "Parts Catalog", permission: "section.settings_pricing.view" },
      // ⚠️ No PMO Tools entry since Sep 18, 2026 — it is a button of its own
      // next to the search bar, opening a floating window that stays put
      // while you work (components/tools/PmoToolsWindow.jsx). /tools still
      // exists for old links, but a menu entry beside the button would be two
      // doors to the same room, one of which loses your place.
    ],
  },
  // Templates + departments + customers + users + roles. The umbrella marker is
  // written whenever any Settings sub-section is granted.
  { to: "/settings", label: "Settings", permission: "section.settings.view" },
];


// Is the browser on this nav child's page? `matchPaths` covers a page whose URL
// is not under the child's own `to` — /properties/:id belongs to Customers. A
// `to` with a query (a Settings shortcut) matches that Settings page, plus any
// in its `sections`.
function onChildPath(child, { pathname, search }) {
  const [base, query] = child.to.split("?");
  if (query) {
    const section = new URLSearchParams(search).get("section");
    const mine = [new URLSearchParams(query).get("section"), ...(child.sections || [])];
    if (pathname === base && mine.includes(section)) return true;
    return (child.matchPaths || []).some((m) => pathname === m || pathname.startsWith(`${m}/`));
  }
  return [base, ...(child.matchPaths || [])].some(
    (b) => pathname === b || pathname.startsWith(`${b}/`)
  );
}

const Caret = ({ open }) => (
  <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
);

// Desktop dropdown for a nav group. The header is `overflow-hidden`, so the
// panel is viewport-`fixed` and positioned from the trigger rect (the same
// approach FilterViews uses) — an `absolute` panel inside a positioned wrapper
// would be clipped by the header bar.
function NavGroup({ group, isActive, location }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      const M = 8;
      const r = el.getBoundingClientRect();
      const width = 208; // 13rem
      setPos({
        top: r.bottom + 6,
        left: Math.max(M, Math.min(r.left, window.innerWidth - width - M)),
        width,
      });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);

  return (
    <div ref={ref} className="shrink-0">
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors ${
          isActive
            ? "text-ink border-b-2 border-brand-600 pb-0.5"
            : "text-ink-muted hover:text-ink"
        }`}
      >
        {group.label}
        <Caret open={open} />
      </button>

      {open && pos && (
        <div
          role="menu"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          className="fixed z-panel bg-surface border border-line rounded-lg shadow-lg py-1"
        >
          {group.children.map((child) => {
            const childActive = onChildPath(child, location);
            return (
              <Link
                key={child.to}
                to={child.to}
                onClick={() => setOpen(false)}
                aria-current={childActive ? "page" : undefined}
                className={`block px-3 py-2 text-sm transition-colors ${
                  childActive
                    ? "text-brand-700 bg-brand-50 font-medium"
                    : "text-ink-muted hover:bg-surface-inset hover:text-ink"
                }`}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Header({ pmoToolsOpen = false, onTogglePmoTools }) {
  const { me } = useAuth();
  const { can, isPartner } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMobileGroups, setOpenMobileGroups] = useState(() => new Set());
  const [viewAsOpen, setViewAsOpen] = useState(false);
  // The menu (below xl) closes on an outside press, Escape, or any navigation —
  // same pattern as NavGroup. Both the button and the panel count as "inside".
  const menuButtonRef = useRef(null);
  const menuPanelRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuButtonRef.current?.contains(e.target)) return;
      if (menuPanelRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // ⚠️ `me.role` reflects the TARGET while View As is active (get_current_user
  // swaps `uid` at the chokepoint, and everything downstream — including this
  // — is keyed off that). Checking `!me?.view_as` first is what hides the
  // launcher while already viewing as someone, admin target or not: nesting
  // impersonation would make "who is really here" and the Exit flow murkier
  // for no real benefit. This is a hardcoded role check on purpose, not a
  // permission — see firebase_auth.py's View As invariant.
  const canStartViewAs = !me?.view_as && me?.role === "system_admin";

  const allowed = (link) =>
    (!link.permission || can(link.permission)) && !(link.hideForPartners && isPartner);

  // ⚠️ A group has no permission of its own — it is shown iff a child survives.
  // That is exactly what the old role-name `hideForRoles` was faking: hide
  // every child and the group collapses on its own.
  const visibleLinks = NAV_LINKS.map((link) =>
    link.children ? { ...link, children: link.children.filter(allowed) } : link
  ).filter((link) => (link.children ? link.children.length > 0 : allowed(link)));

  // A group reads as active while the user is on one of its pages.
  const groupIsActive = (group) => group.children.some((c) => onChildPath(c, location));
  // On a Settings page a group links to (Customers, Rate Cards…), that group is
  // the active one, not Settings.
  const claimedByGroup = NAV_LINKS.some((l) => l.children && l.children.some((c) => c.to.includes("?") && onChildPath(c, location)));
  const topActive = (link, isActive) => isActive && !(link.to === "/settings" && claimedByGroup);

  // Opening the mobile menu while on a grouped page expands that group, so the
  // current page is visible without a second tap.
  useEffect(() => {
    if (!menuOpen) return;
    const active = NAV_LINKS.filter(
      (l) => l.children && l.children.some((c) => onChildPath(c, location))
    ).map((l) => l.label);
    if (active.length) setOpenMobileGroups((prev) => new Set([...prev, ...active]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen, pathname, location.search]);

  const toggleMobileGroup = (label) =>
    setOpenMobileGroups((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  const linkClass = ({ isActive }) =>
    `text-sm font-medium whitespace-nowrap transition-colors ${
      isActive
        ? "text-ink border-b-2 border-brand-600 pb-0.5"
        : "text-ink-muted hover:text-ink"
    }`;

  return (
    <header className="bg-surface border-b border-line shadow-sm px-3 sm:px-4 h-14 flex items-center gap-2 sm:gap-3 overflow-hidden shrink-0">
      {/* Logo + back/forward — never shrinks */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <img src={logo} alt="STINTE" className="h-6 sm:h-7 w-auto" />

        {/* Browser-style page history */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 sm:p-0 sm:w-7 sm:h-7 flex items-center justify-center rounded text-ink-muted hover:text-ink hover:bg-surface-inset transition-colors"
            aria-label="Go back"
            title="Back"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            onClick={() => navigate(1)}
            // Phones have their own forward gesture, and the header has no room
            // for it there — the menu button was being pushed off-screen.
            className="hidden sm:flex sm:w-7 sm:h-7 items-center justify-center rounded text-ink-muted hover:text-ink hover:bg-surface-inset transition-colors"
            aria-label="Go forward"
            title="Forward"
          >
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Below xl the tabs don't fit, so every link lives in this menu. It
            sits where the tabs would be (owner ask, Oct 13 2026) — it used to
            be an unlabeled icon at the far right, so on a smaller window the
            navigation looked like it had vanished. Label at sm:+, icon-only on
            a phone, where the header has no room for it. */}
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label="Navigation menu"
          className={`xl:hidden flex items-center gap-1.5 rounded-lg border p-2 sm:px-2.5 sm:py-1.5 text-sm font-medium transition-colors ${
            menuOpen
              ? "bg-brand-50 text-brand-700 border-brand-300"
              : "bg-surface-inset text-ink-muted border-line hover:bg-zinc-200 hover:text-ink"
          }`}
        >
          {menuOpen
            ? <X className="w-4 h-4 shrink-0" aria-hidden="true" />
            : <Menu className="w-4 h-4 shrink-0" aria-hidden="true" />}
          <span className="hidden sm:inline">Menu</span>
        </button>
      </div>

      {/* Desktop nav — xl+ only. Below 1280px the links don't fit beside the
          search box and buttons (measured: 39px of room for 619px of links at
          1024), so the hamburger menu, which holds every link, takes over. */}
      <nav className="hidden xl:flex items-center gap-4 flex-1 min-w-0 overflow-x-auto">
        {visibleLinks.map((link) =>
          link.children ? (
            <NavGroup key={link.label} group={link} isActive={groupIsActive(link)} location={location} />
          ) : (
            <NavLink key={link.to} to={link.to} className={({ isActive }) => linkClass({ isActive: topActive(link, isActive) })}>
              {link.label}
            </NavLink>
          )
        )}
      </nav>
      {/* Below xl the nav is hidden; this keeps the controls right-aligned. */}
      <div className="flex-1 xl:hidden" aria-hidden="true" />

      {/* PMO Tools — the one nav item that is a button, not a link: it opens a
          floating window over the current page instead of navigating away.
          Label at lg:+, icon-only below that so the header never overflows. */}
      {can("section.pmo_tools.view") && onTogglePmoTools && (
        <button
          type="button"
          onClick={onTogglePmoTools}
          aria-pressed={pmoToolsOpen}
          aria-label="PMO Tools"
          title="PMO Tools"
          className={`hidden md:flex shrink-0 items-center gap-1.5 rounded-lg border px-2 min-[1760px]:px-2.5 py-1.5
                      text-xs font-medium transition-colors ${
                        pmoToolsOpen
                          ? "bg-brand-50 text-brand-700 border-brand-300"
                          : "bg-surface-inset text-ink-muted border-line hover:bg-zinc-200 hover:text-ink"
                      }`}
        >
          <Wrench className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span className="hidden min-[1760px]:inline whitespace-nowrap">PMO Tools</span>
        </button>
      )}

      {/* View As — system_admin only, hidden while already viewing as
          someone (see canStartViewAs). Label at lg:+, icon-only below that,
          same responsive shape as the PMO Tools button beside it. */}
      {canStartViewAs && (
        <button
          type="button"
          onClick={() => setViewAsOpen(true)}
          aria-label="View As"
          title="View as another user"
          className="hidden md:flex shrink-0 items-center gap-1.5 rounded-lg border px-2 min-[1760px]:px-2.5 py-1.5
                     text-xs font-medium transition-colors bg-surface-inset text-ink-muted
                     border-line hover:bg-zinc-200 hover:text-ink"
        >
          <Eye className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span className="hidden min-[1760px]:inline whitespace-nowrap">View As</span>
        </button>
      )}

      {/* Universal search — always at the top of the page, on every route. A
          plain inline box at sm:+; below that it collapses to an icon that
          opens /search directly, since a page-header-width input has nowhere
          to go on a phone-width header. */}
      <GlobalSearchBar className="hidden sm:block w-40 md:w-56 xl:w-48 min-[1760px]:w-64 shrink-0" />
      <button
        onClick={() => navigate("/search")}
        className="sm:hidden shrink-0 p-2.5 text-ink-muted hover:text-ink hover:bg-surface-inset rounded transition-colors"
        aria-label="Search"
        title="Search"
      >
        <Search className="w-4 h-4" aria-hidden="true" />
      </button>

      {/* Right side — bell + profile avatar pill; Sign Out lives on the profile page */}
      <div className="flex items-center gap-2 shrink-0">
        {me && <NotificationBell />}
        {me && (
          <Link
            to={`/profile/${me.uid}`}
            title={me.partner_tech_name ? `${me.partner_tech_name} (${me.name})` : `My profile (${me.email})`}
            aria-label="My profile"
            className="hidden sm:flex items-center gap-2 text-xs text-ink-muted hover:text-ink bg-surface-inset hover:bg-zinc-200 pl-1.5 pr-1.5 min-[1760px]:pr-2.5 py-1 rounded-full transition-colors"
          >
            {/* Photo, or initials when they have none */}
            <UserAvatar user={me} size="xs" />
            <span className="hidden min-[1760px]:inline max-w-[140px] truncate">{me.email}</span>
            {/* A partner company account is shared — show which tech this session is. */}
            {me.partner_tech_name && (
              <span className="max-w-[160px] truncate text-ink">{me.partner_tech_name} · {me.name}</span>
            )}
          </Link>
        )}

      </div>

      {/* The menu panel: a full-width sheet on a phone, a dropdown under the
          button at sm:+. `absolute top-14` escapes the header's overflow-hidden
          because the header itself is not positioned. */}
      {menuOpen && (
        <div
          ref={menuPanelRef}
          className="absolute top-14 left-0 right-0 bg-surface border-b border-line shadow-lg z-panel flex flex-col xl:hidden
                     sm:top-[3.75rem] sm:left-3 sm:right-auto sm:w-72 sm:border sm:rounded-lg
                     sm:max-h-[calc(100vh-4.5rem)] sm:overflow-y-auto"
        >
          {visibleLinks.map((link) =>
            link.children ? (
              // Groups expand in place rather than opening a second overlay.
              <div key={link.label} className="border-b border-zinc-100">
                <button
                  type="button"
                  onClick={() => toggleMobileGroup(link.label)}
                  aria-expanded={openMobileGroups.has(link.label)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm ${
                    groupIsActive(link) ? "text-brand-700 bg-brand-50" : "text-ink-muted hover:bg-zinc-50 hover:text-ink"
                  }`}
                >
                  {link.label}
                  <Caret open={openMobileGroups.has(link.label)} />
                </button>
                {openMobileGroups.has(link.label) &&
                  link.children.map((child) => {
                    const childActive = onChildPath(child, location);
                    return (
                      <Link
                        key={child.to}
                        to={child.to}
                        aria-current={childActive ? "page" : undefined}
                        className={`block pl-8 pr-4 py-2.5 text-sm bg-surface-muted ${
                          childActive ? "text-brand-700 font-medium" : "text-ink-muted hover:text-ink"
                        }`}
                        onClick={() => setMenuOpen(false)}
                      >
                        {child.label}
                      </Link>
                    );
                  })}
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm border-b border-zinc-100 ${
                    topActive(link, isActive) ? "text-brand-700 bg-brand-50" : "text-ink-muted hover:bg-zinc-50 hover:text-ink"
                  }`
                }
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            )
          )}
          {can("section.pmo_tools.view") && onTogglePmoTools && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onTogglePmoTools();
              }}
              className="flex items-center gap-2 px-4 py-3 text-sm text-left border-b border-zinc-100 text-ink-muted hover:bg-zinc-50 hover:text-ink"
            >
              <Wrench className="w-4 h-4 shrink-0" aria-hidden="true" />
              PMO Tools
            </button>
          )}
          {canStartViewAs && (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setViewAsOpen(true);
              }}
              className="flex items-center gap-2 px-4 py-3 text-sm text-left border-b border-zinc-100 text-ink-muted hover:bg-zinc-50 hover:text-ink"
            >
              <Eye className="w-4 h-4 shrink-0" aria-hidden="true" />
              View As
            </button>
          )}
          {me && (
            <NavLink
              to={`/profile/${me.uid}`}
              className={({ isActive }) =>
                `px-4 py-3 text-sm border-b border-zinc-100 ${
                  isActive ? "text-brand-700 bg-brand-50" : "text-ink-muted hover:bg-zinc-50 hover:text-ink"
                }`
              }
              onClick={() => setMenuOpen(false)}
            >
              My Profile
            </NavLink>
          )}
        </div>
      )}

      {/* `fixed inset-0` — not clipped by this header's own `overflow-hidden`,
          same reasoning NotificationBell's popover already relies on. */}
      {viewAsOpen && <ViewAsPicker onClose={() => setViewAsOpen(false)} />}
    </header>
  );
}
